import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  assignMaintenanceOrder,
  attachMaintenanceEvidence,
  cancelMaintenanceOrder,
  completeMaintenanceOrder,
  createMaintenanceSchedule,
  getMaintenanceEvidenceUrl,
  listAssets,
  listMaintenanceOrderEvidence,
  listMaintenanceOrders,
  listMaintenanceSchedules,
  openMaintenanceOrder,
  removeMaintenanceEvidence,
  startMaintenanceOrder,
} from '../../api/assets';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  GroupLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDate,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { Asset, MaintenanceOrder, MaintenanceOrderEvidence, MaintenanceOrderType, MaintenanceSchedule } from '../../types/domain';

const ORDER_TYPE_LABEL: Record<MaintenanceOrderType, string> = { PREVENTIVE: 'Preventif', BREAKDOWN: 'Kerusakan' };

/**
 * Assets & Maintenance workspace (FASE 11, migration 0048). Two real views:
 * Work Orders (lifecycle: OPEN -> assign -> IN_PROGRESS -> COMPLETED/
 * CANCELLED) and Preventive Schedules (due/overdue derived deterministically
 * from next_due_date — never an invented interval). AI has no role here:
 * every state transition is a direct, permission-gated human action.
 */
export default function MaintenancePage() {
  const { hasPermission, context } = usePermissions();
  const currentUserId = context && context.authenticated ? context.user_id : null;
  const currentTenantId = context && context.authenticated ? context.tenant_id : null;
  const { activeBranchId } = useBranch();
  const [tab, setTab] = useState<'orders' | 'schedules'>('orders');
  const [orders, setOrders] = useState<MaintenanceOrder[]>([]);
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedOrder, setSelectedOrder] = useState<MaintenanceOrder | null>(null);

  const canCreate = hasPermission('maintenance_order', 'create');
  const canCreateSchedule = hasPermission('maintenance_schedule', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, s, a] = await Promise.all([listMaintenanceOrders(), listMaintenanceSchedules(), listAssets()]);
      setOrders(o);
      setSchedules(s);
      setAssets(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const assetById = useMemo(() => new Map(assets.map((a) => [a.asset_id, a])), [assets]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const asset = assetById.get(o.asset_id);
      if (q && !o.order_no.toLowerCase().includes(q) && !(asset?.name.toLowerCase().includes(q))) return false;
      if (statusFilter !== 'ALL' && o.status !== statusFilter) return false;
      return true;
    });
  }, [orders, search, statusFilter, assetById]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filteredOrders, 20);

  const today = new Date().toISOString().slice(0, 10);
  const overdueSchedules = schedules.filter((s) => s.status === 'ACTIVE' && s.next_due_date < today);
  const dueSoonSchedules = schedules.filter((s) => s.status === 'ACTIVE' && s.next_due_date >= today);

  const openCount = orders.filter((o) => o.status === 'OPEN').length;
  const inProgressCount = orders.filter((o) => o.status === 'IN_PROGRESS').length;
  const completedCount = orders.filter((o) => o.status === 'COMPLETED').length;

  return (
    <div>
      <PageHeader title="Perawatan Aset" subtitle="Work order, jadwal preventif, dan due/overdue. Migrasi 0048." />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button variant={tab === 'orders' ? 'primary' : 'secondary'} onClick={() => setTab('orders')}>
          Work Order
        </Button>
        <Button variant={tab === 'schedules' ? 'primary' : 'secondary'} onClick={() => setTab('schedules')}>
          Jadwal Preventif ({overdueSchedules.length} lewat jadwal)
        </Button>
      </div>

      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && tab === 'orders' && (
        <>
          {canCreate && (
            <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
              {showForm ? 'Batal' : '+ Buat Work Order'}
            </Button>
          )}
          {showForm && canCreate && activeBranchId && (
            <NewOrderForm
              branchId={activeBranchId}
              assets={assets}
              schedules={schedules}
              onCreated={() => {
                setShowForm(false);
                void load();
              }}
            />
          )}
          <SummaryStrip
            items={[
              { label: 'Total WO', value: orders.length },
              { label: 'Terbuka', value: openCount, tone: 'info' as const },
              { label: 'Berjalan', value: inProgressCount, tone: 'info' as const },
              { label: 'Selesai', value: completedCount, tone: 'success' as const },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari nomor WO/aset..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 180 }}>
                <option value="ALL">Semua status</option>
                <option value="OPEN">Terbuka</option>
                <option value="IN_PROGRESS">Berjalan</option>
                <option value="COMPLETED">Selesai</option>
                <option value="CANCELLED">Dibatalkan</option>
              </Select>
            }
          />
          {orders.length === 0 && <EmptyState text="Belum ada work order." />}
          {orders.length > 0 && total === 0 && <EmptyState text="Tidak ada work order yang cocok." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. WO</th>
                  <th style={thStyle}>Aset</th>
                  <th style={thStyle}>Tipe</th>
                  <th style={thStyle}>Dibuka</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((o) => (
                  <ClickableRow key={o.order_id} onClick={() => setSelectedOrder(o)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{o.order_no}</td>
                    <td style={tdStyle}>{assetById.get(o.asset_id)?.name ?? '—'}</td>
                    <td style={tdStyle}>{ORDER_TYPE_LABEL[o.order_type]}</td>
                    <td style={tdStyle}>{formatDate(o.opened_at)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={o.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      {!loading && !error && tab === 'schedules' && (
        <>
          {canCreateSchedule && (
            <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
              {showForm ? 'Batal' : '+ Jadwal Preventif Baru'}
            </Button>
          )}
          {showForm && canCreateSchedule && (
            <NewScheduleForm
              assets={assets}
              onCreated={() => {
                setShowForm(false);
                void load();
              }}
            />
          )}
          {schedules.length === 0 && <EmptyState text="Belum ada jadwal perawatan preventif." />}
          {overdueSchedules.length > 0 && (
            <>
              <GroupLabel>Lewat Jadwal ({overdueSchedules.length})</GroupLabel>
              <ScheduleTable rows={overdueSchedules} assetById={assetById} overdue />
            </>
          )}
          {dueSoonSchedules.length > 0 && (
            <>
              <GroupLabel>Terjadwal</GroupLabel>
              <ScheduleTable rows={dueSoonSchedules} assetById={assetById} overdue={false} />
            </>
          )}
        </>
      )}

      <OrderDetailDrawer
        order={selectedOrder}
        assetName={selectedOrder ? assetById.get(selectedOrder.asset_id)?.name ?? '—' : ''}
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        currentUserId={currentUserId}
        currentTenantId={currentTenantId}
        canUpdate={hasPermission('maintenance_order', 'update')}
        canComplete={hasPermission('maintenance_order', 'complete')}
        canCancel={hasPermission('maintenance_order', 'cancel')}
        onChanged={() => void load()}
      />
    </div>
  );
}

function ScheduleTable({ rows, assetById, overdue }: { rows: MaintenanceSchedule[]; assetById: Map<string, Asset>; overdue: boolean }) {
  return (
    <table style={{ ...tableStyle, marginBottom: 20 }}>
      <thead>
        <tr>
          <th style={thStyle}>Aset</th>
          <th style={thStyle}>Interval</th>
          <th style={thStyle}>Jatuh Tempo</th>
          <th style={thStyle}>Terakhir Selesai</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.schedule_id}>
            <td style={tdStyle}>{assetById.get(s.asset_id)?.name ?? '—'}</td>
            <td style={tdStyle}>{s.interval_days} hari</td>
            <td style={{ ...tdStyle, color: overdue ? 'var(--alex-danger, #d33)' : undefined, fontWeight: overdue ? 600 : 400 }}>{formatDate(s.next_due_date)}</td>
            <td style={tdStyle}>{s.last_done_date ? formatDate(s.last_done_date) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NewOrderForm({
  branchId,
  assets,
  schedules,
  onCreated,
}: {
  branchId: string;
  assets: Asset[];
  schedules: MaintenanceSchedule[];
  onCreated: () => void;
}) {
  const [assetId, setAssetId] = useState('');
  const [orderType, setOrderType] = useState<MaintenanceOrderType>('BREAKDOWN');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkedSchedule = orderType === 'PREVENTIVE' ? schedules.find((s) => s.asset_id === assetId && s.status === 'ACTIVE') ?? null : null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!assetId) return;
    setSubmitting(true);
    setError(null);
    try {
      await openMaintenanceOrder(branchId, assetId, orderType, linkedSchedule?.schedule_id ?? null, notes.trim() || null);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <FieldLabel label="Aset">
          <Select value={assetId} onChange={(e) => setAssetId(e.target.value)} required>
            <option value="">Pilih aset...</option>
            {assets
              .filter((a) => a.status !== 'DISPOSED')
              .map((a) => (
                <option key={a.asset_id} value={a.asset_id}>
                  {a.asset_tag} — {a.name}
                </option>
              ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Tipe">
          <Select value={orderType} onChange={(e) => setOrderType(e.target.value as MaintenanceOrderType)}>
            <option value="BREAKDOWN">Kerusakan</option>
            <option value="PREVENTIVE">Preventif</option>
          </Select>
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <FieldLabel label="Catatan">
          <TextInput value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Deskripsi singkat" />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Buat
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function NewScheduleForm({ assets, onCreated }: { assets: Asset[]; onCreated: () => void }) {
  const [assetId, setAssetId] = useState('');
  const [intervalDays, setIntervalDays] = useState('30');
  const [nextDueDate, setNextDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!assetId || !nextDueDate) return;
    setSubmitting(true);
    setError(null);
    try {
      await createMaintenanceSchedule(assetId, Number(intervalDays), nextDueDate);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <FieldLabel label="Aset">
          <Select value={assetId} onChange={(e) => setAssetId(e.target.value)} required>
            <option value="">Pilih aset...</option>
            {assets
              .filter((a) => a.status !== 'DISPOSED')
              .map((a) => (
                <option key={a.asset_id} value={a.asset_id}>
                  {a.asset_tag} — {a.name}
                </option>
              ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 140 }}>
        <FieldLabel label="Interval (hari)">
          <TextInput type="number" min="1" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 170 }}>
        <FieldLabel label="Jatuh Tempo Berikutnya">
          <TextInput type="date" value={nextDueDate} onChange={(e) => setNextDueDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function OrderDetailDrawer({
  order,
  assetName,
  open,
  onClose,
  currentUserId,
  currentTenantId,
  canUpdate,
  canComplete,
  canCancel,
  onChanged,
}: {
  order: MaintenanceOrder | null;
  assetName: string;
  open: boolean;
  onClose: () => void;
  currentUserId: string | null;
  currentTenantId: string | null;
  canUpdate: boolean;
  canComplete: boolean;
  canCancel: boolean;
  onChanged: () => void;
}) {
  const [evidence, setEvidence] = useState<MaintenanceOrderEvidence[]>([]);
  const [cost, setCost] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceUrls, setEvidenceUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!order) return;
    setError(null);
    setCost('');
    setCancelReason('');
    setEvidenceNote('');
    setEvidenceFile(null);
    setEvidenceUrls({});
    listMaintenanceOrderEvidence(order.order_id)
      .then(setEvidence)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [order?.order_id]);

  if (!order) return null;

  const run = async (fn: () => Promise<void>, close = true) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      if (close) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={order.order_no}>
      <DrawerField label="Aset" value={assetName} />
      <DrawerField label="Tipe" value={ORDER_TYPE_LABEL[order.order_type]} />
      <DrawerField label="Status" value={<StatusBadge status={order.status} />} />
      <DrawerField label="Dibuka" value={formatDateTime(order.opened_at)} />
      {order.started_at && <DrawerField label="Dimulai" value={formatDateTime(order.started_at)} />}
      {order.completed_at && <DrawerField label="Selesai" value={formatDateTime(order.completed_at)} />}
      {order.cost != null && <DrawerField label="Biaya" value={`Rp ${Number(order.cost).toLocaleString('id-ID')}`} />}
      {order.notes && <DrawerField label="Catatan" value={order.notes} />}

      {canUpdate && order.status === 'OPEN' && !order.assigned_to && currentUserId && (
        <Button
          variant="secondary"
          loading={busy}
          onClick={() => void run(() => assignMaintenanceOrder(order.order_id, currentUserId), false)}
          style={{ marginTop: 12, marginRight: 8 }}
        >
          Tugaskan ke Saya
        </Button>
      )}
      {canUpdate && order.status === 'OPEN' && (
        <Button variant="secondary" loading={busy} onClick={() => void run(() => startMaintenanceOrder(order.order_id), false)} style={{ marginTop: 12 }}>
          Mulai Kerjakan
        </Button>
      )}

      {canComplete && (order.status === 'OPEN' || order.status === 'IN_PROGRESS') && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <FieldLabel label="Selesaikan Work Order — Biaya">
            <div style={{ display: 'flex', gap: 8 }}>
              <TextInput type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" style={{ flex: 1 }} />
              <Button
                variant="primary"
                loading={busy}
                onClick={() => void run(() => completeMaintenanceOrder(order.order_id, Number(cost || 0)))}
              >
                Selesaikan
              </Button>
            </div>
          </FieldLabel>
        </div>
      )}

      {canCancel && (order.status === 'OPEN' || order.status === 'IN_PROGRESS') && (
        <div style={{ marginTop: 14 }}>
          <FieldLabel label="Batalkan — Alasan">
            <div style={{ display: 'flex', gap: 8 }}>
              <TextInput value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Alasan pembatalan" style={{ flex: 1 }} />
              <Button
                variant="secondary"
                loading={busy}
                onClick={() => void run(() => cancelMaintenanceOrder(order.order_id, cancelReason.trim() || 'Tidak ada alasan'))}
              >
                Batalkan
              </Button>
            </div>
          </FieldLabel>
        </div>
      )}

      {canUpdate && currentTenantId && (order.status === 'OPEN' || order.status === 'IN_PROGRESS') && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <FieldLabel label="Unggah Bukti (foto/dokumen)">
            <input
              type="file"
              onChange={(e) => setEvidenceFile(e.target.files?.[0] ?? null)}
              style={{ display: 'block', marginBottom: 8, fontSize: 'var(--alex-fs-body)' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <TextInput value={evidenceNote} onChange={(e) => setEvidenceNote(e.target.value)} placeholder="Keterangan (opsional)" style={{ flex: 1 }} />
              <Button
                variant="secondary"
                loading={busy}
                disabled={!evidenceFile}
                onClick={() =>
                  void run(async () => {
                    if (!evidenceFile || !currentTenantId) return;
                    await attachMaintenanceEvidence(currentTenantId, order.order_id, evidenceFile, evidenceNote.trim() || null);
                    setEvidenceFile(null);
                    setEvidenceNote('');
                    setEvidence(await listMaintenanceOrderEvidence(order.order_id));
                  }, false)
                }
              >
                Unggah
              </Button>
            </div>
          </FieldLabel>
        </div>
      )}
      {error && <ErrorNote text={error} />}

      {evidence.length > 0 && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <GroupLabel>Bukti/Dokumen Pekerjaan</GroupLabel>
          {evidence.map((ev) => (
            <div key={ev.evidence_id} style={{ padding: '6px 0', fontSize: 'var(--alex-fs-body)', opacity: ev.status === 'REMOVED' ? 0.5 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span>
                  {ev.original_filename ?? ev.description ?? '—'}
                  {ev.status === 'REMOVED' && ' (dihapus)'}
                  {' '}
                  <span style={{ color: 'var(--alex-text-secondary)' }}>({formatDateTime(ev.uploaded_at)})</span>
                </span>
                {ev.status === 'ACTIVE' && ev.storage_path && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        void (async () => {
                          try {
                            const url = await getMaintenanceEvidenceUrl(ev.storage_path!);
                            setEvidenceUrls((prev) => ({ ...prev, [ev.evidence_id]: url }));
                          } catch (err) {
                            setError(err instanceof Error ? err.message : String(err));
                          }
                        })()
                      }
                    >
                      Lihat
                    </Button>
                    {canUpdate && (
                      <Button
                        variant="secondary"
                        loading={busy}
                        onClick={() =>
                          void run(async () => {
                            await removeMaintenanceEvidence(ev.evidence_id);
                            setEvidence(await listMaintenanceOrderEvidence(order.order_id));
                          }, false)
                        }
                      >
                        Hapus
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {evidenceUrls[ev.evidence_id] && (
                <a href={evidenceUrls[ev.evidence_id]} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-primary)' }}>
                  Buka tautan (berlaku 5 menit)
                </a>
              )}
              {ev.description && ev.original_filename && <div style={{ color: 'var(--alex-text-secondary)' }}>{ev.description}</div>}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}
